import { useQuery } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { api } from "@/lib/client"
import { formatAddress, formatDateLabel, formatMoney } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"
import { ADMIN_TABLE_CLASSNAME, ADMIN_TABLE_HEAD_TH, ADMIN_TABLE_ROW, statusBadgeClass } from "@/lib/table"

export function CustomerDetailPage() {
  const { customerId = "" } = useParams()
  const query = useQuery({
    queryFn: () => api.customer(customerId),
    queryKey: queryKeys.customer(customerId),
  })
  const customer = query.data?.customer

  if (query.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>
  if (!customer) return <p className="text-sm text-destructive">Customer not found.</p>

  return (
    <div className="space-y-6">
      <AdminPageHeader description={customer.email ?? undefined} title={customer.name || "Customer"} />
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p>{customer.email || "No email"}</p>
            <p className="text-muted-foreground">{customer.phone || "No phone"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Last address</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {formatAddress(customer.lastAddress)}
          </CardContent>
        </Card>
      </div>
      <div className="overflow-hidden rounded-lg border border-border">
        <Table className={ADMIN_TABLE_CLASSNAME}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Order</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Week</TableHead>
              <TableHead className={`${ADMIN_TABLE_HEAD_TH} text-right`}>Total</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(customer.orders ?? []).length === 0 ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={4}>
                  No orders.
                </TableCell>
              </TableRow>
            ) : (
              (customer.orders ?? []).map((order) => (
                <TableRow className={ADMIN_TABLE_ROW} key={order.id}>
                  <TableCell className="font-medium">
                    <Link to={`/orders/${order.id}`}>{order.name}</Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateLabel(order.weekStartDate)} – {formatDateLabel(order.weekEndDate)}
                  </TableCell>
                  <TableCell className="text-right">{formatMoney(order.amountTotal, order.currency)}</TableCell>
                  <TableCell>
                    <span className={statusBadgeClass(order.state)}>{order.state}</span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
